import { useCallback, useEffect, useState } from "react";
import { useMqttContext } from "../contexts/MqttContext";
import {
  parseRadioTranscriptPayload,
  radioTranscriptKey,
  type RadioTranscript,
} from "../types/radio";

export const MAX_RADIO_TRANSCRIPTS = 200;

function appendUnique(prev: RadioTranscript[], next: RadioTranscript): RadioTranscript[] {
  const key = radioTranscriptKey(next);
  if (prev.some((t) => radioTranscriptKey(t) === key)) return prev;
  const merged = [...prev, next];
  return merged.length > MAX_RADIO_TRANSCRIPTS
    ? merged.slice(merged.length - MAX_RADIO_TRANSCRIPTS)
    : merged;
}

/**
 * Radio transcript log.
 *
 * MQTT `{prefix}radio/transcript` is the source of truth so every dashboard
 * sees every utterance; `addLocal` lets the capturing browser show its own WS
 * echo immediately and the later MQTT copy is deduped by `ts + client`.
 *
 * Must be used inside an `<MqttProvider>`.
 */
export function useRadioTranscripts(topicPrefix: string) {
  const [transcripts, setTranscripts] = useState<RadioTranscript[]>([]);

  const { status: connectionStatus, subscribe } = useMqttContext();

  const handleMessage = useCallback((topic: string, payload: string) => {
    if (topic !== `${topicPrefix}radio/transcript`) return;
    const transcript = parseRadioTranscriptPayload(payload);
    if (!transcript) return;
    setTranscripts((prev) => appendUnique(prev, transcript));
  }, [topicPrefix]);

  useEffect(() => subscribe(handleMessage), [subscribe, handleMessage]);

  const addLocal = useCallback((transcript: RadioTranscript) => {
    setTranscripts((prev) => appendUnique(prev, transcript));
  }, []);

  const clear = useCallback(() => setTranscripts([]), []);

  return { transcripts, addLocal, clear, connectionStatus };
}
